/**
 * Machine à états d'appel — SEULE source de vérité côté frontend v2.
 *
 *   idle ──► preview ──► dialing ──► ringing ──► active ──► wrapup ──► idle
 *     │                    └────────────┴──────────┘           ▲
 *     └──► incoming ─────────────────────► active ─────────────┘
 *
 * Remplace les useState/refs éparpillés des 3 pages v1 (currentCallIdRef,
 * userInitiatedHangupRef…). Le `callId` est connu dès le départ — créé avant
 * `device.connect()` pour un sortant, transmis par le `<Parameter callId>` du
 * TwiML pour un entrant : plus jamais de `findRecentCall` ni de
 * retry-setTimeout.
 *
 * Exposée en store externe (`useSyncExternalStore`) : l'état survit à la
 * navigation entre pages, le badge et les pages sont des vues pures dessus.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE QUE CE FICHIER GARANTIT (leçons de la recette réelle du 2026-07-22)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. AUCUNE transition n'est ignorée en silence. Une transition illégale est
 *    loguée AVEC son contexte (phase courante, cible, callId) et son verdict
 *    est RENDU à l'appelant, qui doit en tenir compte — c'est ce que
 *    `device.ts` fait pour un entrant refusé : `call.reject()`, pour que
 *    Twilio re-route immédiatement vers un autre agent.
 *
 * 2. Un événement RÉPÉTÉ n'est pas une erreur. La recette a produit un
 *    « transition refusée ringing→ringing » alarmant alors qu'il s'agissait du
 *    même fait annoncé deux fois (événement SDK + push WebSocket). Une
 *    transition vers la phase COURANTE est désormais un no-op silencieux.
 *
 * 3. La machine ne peut pas rester coincée. Toute fin d'appel mène à `wrapup`
 *    (qualification attendue) ou à `idle`, et `wrapup` est toujours
 *    quittable — par l'outcome (`reset`) ou par abandon explicite
 *    (`abandonWrapup`). Un état résiduel bloquait les entrants suivants
 *    (constat F-8).
 *
 * 4. Une erreur reste VISIBLE. L'ancienne implémentation posait `error` puis
 *    appelait `reset()`, qui restaurait l'état initial — donc effaçait
 *    l'erreur dans le même tour de notification : la bannière rouge de la page
 *    n'a jamais pu s'afficher. `fail()` remet bien la machine au repos, mais
 *    en CONSERVANT l'erreur jusqu'à la prochaine action de l'agent.
 *
 * 5. Le COMPTE À REBOURS de l'auto-dial vit ICI (phase `preview`), pas dans un
 *    composant. En v1 il était détenu par `NextContactPreview`, qui déclenchait
 *    l'appel en SIMULANT un clic sur une ref de bouton : la pause, la reprise
 *    et l'annulation dépendaient donc du cycle de vie d'un rendu React.
 *    La machine ne compte pas le temps elle-même (aucune minuterie dans ce
 *    module, il reste pur et testable) : elle expose `tickPreview()`, que
 *    l'orchestrateur appelle une fois par seconde, et c'est ELLE qui décide
 *    quand le rebours est échu.
 */

import { UNREACHABLE_DIAL_FAILURE, type DialFailure } from './dialFailure'

export type CallPhase =
  | 'idle'
  | 'preview' // contact suivant présenté, compte à rebours avant appel auto
  | 'dialing' // Call créé, device.connect() en cours
  | 'ringing' // le destinataire sonne
  | 'active' // en ligne
  | 'wrapup' // appel terminé, saisie d'outcome en attente
  | 'incoming' // appel entrant qui sonne chez l'agent

/** Compte à rebours de la préview auto-dial. `null` hors phase `preview`. */
export interface PreviewState {
  /** Secondes restantes avant l'appel automatique. 0 ⇒ échu. */
  remainingSeconds: number
  /** Durée initiale — sert à l'anneau de progression, jamais au décompte. */
  totalSeconds: number
  /** L'agent a mis l'enchaînement en pause : le rebours ne descend plus. */
  paused: boolean
  /**
   * L'échéance a DÉJÀ été annoncée. Porté par l'état (et non déduit de
   * `remainingSeconds === 0`) pour deux raisons : une préview armée à 0 seconde
   * doit échoir — elle ne le faisait jamais —, et un tic en retard ne doit pas
   * pouvoir ré-annoncer une échéance déjà servie.
   */
  dueFired: boolean
}

export interface CallState {
  phase: CallPhase
  callId: string | null
  twilioSid: string | null
  direction: 'OUTBOUND' | 'INBOUND' | null
  phoneNumber: string | null
  contactId: string | null
  contactName: string | null
  /** Campagne de l'appel — pilote le script affiché pendant la conversation. */
  campaignId: string | null
  /** Ligne `CampaignContact` — c'est elle qui avance quand l'outcome est posé. */
  campaignContactId: string | null
  /** Timestamp ms du passage en `active` — base du chronomètre d'appel. */
  startedAt: number | null
  /**
   * Timestamp ms de la fin de conversation. FIGÉ à l'entrée en `wrapup` : la
   * durée montrée dans le formulaire de qualification doit être celle de
   * l'appel, pas celle qui court pendant que l'agent saisit son résultat.
   */
  endedAt: number | null
  isMuted: boolean
  preview: PreviewState | null
  /**
   * Dernière erreur à montrer à l'agent, sous forme de **CLÉ i18n** du
   * namespace `telephony` (jamais un texte). La machine et le provider ne
   * fabriquent aucune phrase : c'est la scène qui traduit, sinon le mode
   * anglais reste troué là où ça compte le plus — les messages d'échec.
   * Survit au retour à `idle` (cf. point 4) et n'est effacée que par une
   * nouvelle action.
   */
  error: string | null
  /**
   * Code Twilio brut qui accompagne `error` quand il existe (issue #144) :
   * interpolé `{{code}}` dans la clé traduite, pour que le cas « inconnu »
   * affiche le code réel. Posé UNIQUEMENT par `fail()`, effacé avec `error`.
   */
  errorCode: string | null
  /**
   * L'appel courant (ou le dernier) N'A JAMAIS ABOUTI : occupé, numéro
   * invalide, passerelle qui raccroche. Non nul UNIQUEMENT en `wrapup`, et
   * seulement pour ce cas — c'est ce qui permet à la scène de proposer la
   * qualification en UN clic au lieu du formulaire complet. Cf. `dialFailure.ts`.
   */
  dialFailure: DialFailure | null
}

const initialState: CallState = {
  phase: 'idle',
  callId: null,
  twilioSid: null,
  direction: null,
  phoneNumber: null,
  contactId: null,
  contactName: null,
  campaignId: null,
  campaignContactId: null,
  startedAt: null,
  endedAt: null,
  isMuted: false,
  preview: null,
  error: null,
  errorCode: null,
  dialFailure: null,
}

type Listener = () => void

let state: CallState = initialState
const listeners = new Set<Listener>()

function notify(): void {
  // Copie : un abonné qui se désabonne pendant la diffusion ne casse pas
  // l'itération (même précaution que les sets d'abonnés de `device.ts`).
  Array.from(listeners).forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error('[telephony-v2] Abonné callMachine en erreur', error)
    }
  })
}

function setState(partial: Partial<CallState>): void {
  state = { ...state, ...partial }
  notify()
}

/**
 * Transitions autorisées.
 *
 * `dialing`/`ringing` → `wrapup` est volontaire : un sortant sans réponse ou
 * occupé se qualifie lui aussi (NO_ANSWER, BUSY sont des résultats d'appel
 * pour le CRM). `incoming` → `wrapup` l'est tout autant : un entrant raccroché
 * par l'appelant pendant la sonnerie est un appel manqué que l'agent peut
 * vouloir qualifier — mais `device.ts` choisit `needsOutcome: false` pour ce
 * cas précis, la machine se contente de l'autoriser.
 */
const TRANSITIONS: Record<CallPhase, CallPhase[]> = {
  // `idle` → `wrapup` : REPRISE après rafraîchissement (#220). Un seul chemin
  // y mène, `restoreWrapup()`, qui porte sa propre garde ; aucune fin d'appel
  // ne passe par là (`disconnected()` au repos reste un no-op).
  idle: ['preview', 'dialing', 'incoming', 'wrapup'],
  // `preview` → `wrapup` est volontairement ABSENT : rien n'a été composé, il
  // n'y a aucun `Call` à qualifier. L'agent y est libre, donc un entrant passe.
  preview: ['dialing', 'idle', 'incoming'],
  // `dialing` → `preview` : RAPPEL d'un leg en vol (Stop/Pause pendant la
  // fenêtre de connexion anticipée). Un seul chemin y mène, `abortDialing()`,
  // et il est gardé — personne ne « recule » vers une préview par accident.
  dialing: ['ringing', 'active', 'wrapup', 'idle', 'preview'],
  ringing: ['active', 'wrapup', 'idle'],
  active: ['wrapup', 'idle'],
  wrapup: ['idle', 'incoming'],
  incoming: ['active', 'wrapup', 'idle'],
}

/**
 * Phases où l'agent est RÉELLEMENT indisponible pour un nouvel entrant : il a
 * un appel en cours, ou un autre entrant sonne déjà chez lui.
 *
 * `wrapup` n'en fait délibérément PAS partie : l'agent y est physiquement
 * libre (il saisit un résultat). Le backend l'exclut déjà du routage pendant
 * `AGENT_WRAPUP_EXCLUSION_SECONDS` ; si un entrant arrive malgré tout, c'est
 * que le backend le considère joignable — le refuser ferait perdre un appel
 * client pour protéger un formulaire. Cf. `acceptIncoming` ci-dessous.
 *
 * `preview` non plus : l'agent regarde la fiche du contact SUIVANT, aucune
 * ligne n'est ouverte. Un entrant reçu à ce moment-là est présenté et annule
 * simplement le rebours — un appelant réel passe avant un appel qu'on n'a pas
 * encore composé.
 */
const BUSY_PHASES: CallPhase[] = ['dialing', 'ringing', 'active', 'incoming']

/**
 * @returns `true` si la transition a eu lieu (ou était un no-op idempotent),
 *          `false` si elle a été REFUSÉE. Le refus n'est jamais silencieux :
 *          il est logué, et l'appelant doit le traiter.
 */
function transition(to: CallPhase, partial: Partial<CallState> = {}): boolean {
  if (state.phase === to) {
    /**
     * Même fait annoncé deux fois (événement SDK + push WebSocket) : no-op
     * SILENCIEUX ET SANS ÉCRITURE.
     *
     * Le partial était appliqué ici, et c'était un piège : un second
     * `startOutbound` reçu en phase `dialing` (double tap sur « Appeler
     * maintenant » pendant que le POST du premier était en vol) ÉCRASAIT le
     * `callId` de l'appel réellement en cours par celui du second. Le premier
     * appel Twilio restait vivant, mais la machine ne le désignait plus :
     * l'agent se retrouvait à `idle` avec une ligne ouverte et aucun bouton
     * pour raccrocher.
     *
     * Un événement répété ne porte, par définition, aucune information neuve —
     * les enrichissements réels ont leurs propres méthodes (`attachCallId`,
     * `attachTwilioSid`, `setMuted`).
     */
    return true
  }
  if (!TRANSITIONS[state.phase].includes(to)) {
    console.warn(
      `[telephony-v2] Transition REFUSÉE ${state.phase} → ${to}`,
      { callId: state.callId, twilioSid: state.twilioSid, direction: state.direction },
    )
    return false
  }
  setState({ ...partial, phase: to })
  return true
}

export const callMachine = {
  // --- Store externe (useSyncExternalStore) ---
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getSnapshot(): CallState {
    return state
  },

  // -------------------------------------------------------------------------
  // Sortant
  // -------------------------------------------------------------------------

  /**
   * Le `Call` DB existe déjà (POST /telephony/calls) ; `device.connect()` suit.
   * @returns `false` si l'agent n'est pas au repos — l'appelant ne doit alors
   *          PAS appeler `device.connect()`.
   */
  startOutbound(params: {
    callId: string
    phoneNumber: string
    contactId?: string
    contactName?: string
    campaignId?: string
    campaignContactId?: string
  }): boolean {
    /**
     * Garde EXPLICITE : seuls le repos et une préview arment un sortant.
     *
     * Le no-op de `transition` ne suffisait PAS. Deux `POST /telephony/calls`
     * partis presque ensemble (rebours échu + tap « Appeler maintenant »)
     * reviennent l'un après l'autre : le premier fait `preview → dialing`, le
     * second trouve la machine DÉJÀ en `dialing` — donc `phase === to` — et
     * repartait « vrai » sans rien écrire. Le provider en concluait que son
     * appel était armé et enchaînait sur `device.connect()` : deux legs
     * Twilio, un seul suivi par la machine.
     *
     * En refusant, le provider prend sa branche d'échec, qui ANNULE le `Call`
     * en trop (`cancelCall`) — le second appel n'existe donc jamais.
     */
    if (state.phase !== 'idle' && state.phase !== 'preview') {
      console.warn('[telephony-v2] Sortant REFUSÉ : une ligne est déjà engagée', {
        phase: state.phase,
        currentCallId: state.callId,
        refusedCallId: params.callId,
      })
      return false
    }
    return transition('dialing', {
      callId: params.callId,
      phoneNumber: params.phoneNumber,
      contactId: params.contactId ?? null,
      contactName: params.contactName ?? null,
      campaignId: params.campaignId ?? null,
      campaignContactId: params.campaignContactId ?? null,
      direction: 'OUTBOUND',
      twilioSid: null,
      startedAt: null,
      endedAt: null,
      isMuted: false,
      // Le rebours a rempli son office (ou l'agent a forcé l'appel) : il ne
      // doit plus rien piloter une fois la numérotation lancée.
      preview: null,
      error: null,
      errorCode: null,
      dialFailure: null,
    })
  },

  // -------------------------------------------------------------------------
  // Préview auto-dial (mode campagne)
  // -------------------------------------------------------------------------

  /**
   * Présente le contact SUIVANT et arme le compte à rebours.
   *
   * Les coordonnées vont dans les champs habituels (`phoneNumber`,
   * `contactName`, `campaignId`…) et NON dans un sous-objet dédié : une
   * préview est l'identité du prochain appel, et `dial()` la lit telle quelle
   * au moment de composer. Aucune donnée n'est recopiée d'un objet à l'autre,
   * donc aucune divergence possible entre ce qui est affiché et ce qui est
   * composé.
   *
   * @returns `false` si l'agent n'est pas au repos — l'enchaînement doit alors
   *          attendre (il ne doit surtout pas se superposer à un appel).
   */
  startPreview(params: {
    phoneNumber: string
    countdownSeconds: number
    contactId?: string
    contactName?: string
    campaignId?: string
    campaignContactId?: string
  }): boolean {
    // Garde EXPLICITE plutôt que le no-op de `transition` : armer une préview
    // par-dessus une préview existante renverrait « vrai » sans rien écrire
    // (depuis le durcissement ci-dessus), et l'orchestrateur afficherait alors
    // une fiche contact que la machine ne connaît pas — donc un autre numéro
    // composé que celui montré à l'agent.
    if (state.phase !== 'idle') {
      console.warn('[telephony-v2] Préview REFUSÉE : le poste n’est pas au repos', {
        phase: state.phase,
        callId: state.callId,
      })
      return false
    }
    const total = Math.max(0, Math.floor(params.countdownSeconds))
    return transition('preview', {
      callId: null,
      twilioSid: null,
      direction: 'OUTBOUND',
      phoneNumber: params.phoneNumber,
      contactId: params.contactId ?? null,
      contactName: params.contactName ?? null,
      campaignId: params.campaignId ?? null,
      campaignContactId: params.campaignContactId ?? null,
      startedAt: null,
      endedAt: null,
      isMuted: false,
      preview: {
        remainingSeconds: total,
        totalSeconds: total,
        paused: false,
        dueFired: false,
      },
      error: null,
      errorCode: null,
      dialFailure: null,
    })
  },

  /**
   * Fait avancer le rebours d'UNE seconde. Appelé par l'orchestrateur, qui
   * possède la minuterie ; la machine, elle, reste sans horloge (donc testable
   * sans attendre et sans faux positifs de synchronisation).
   *
   * @returns `true` quand le rebours vient d'atteindre 0 — SIGNAL UNIQUE :
   *          les tics suivants renvoient `false` tant que la phase n'a pas
   *          changé. Sans cela, un tic en retard déclencherait un second appel
   *          pour le même contact.
   */
  tickPreview(): boolean {
    const { phase, preview } = state
    if (phase !== 'preview' || !preview || preview.paused) return false
    if (preview.dueFired) return false
    // `max(0, …)` : une préview armée à 0 seconde (réglage à 0, ou valeur
    // aberrante venue du localStorage) échoit au premier tic au lieu de rester
    // figée pour toujours en attendant un appel que personne ne déclenchera.
    const remainingSeconds = Math.max(0, preview.remainingSeconds - 1)
    const dueFired = remainingSeconds === 0
    setState({ preview: { ...preview, remainingSeconds, dueFired } })
    return dueFired
  },

  /**
   * RAPPEL D'UN LEG EN VOL — l'agent a arrêté ou mis en pause pendant la
   * fenêtre de connexion anticipée (cf. `prelaunch.ts`).
   *
   * ══════════════════════════════════════════════════════════════════════════
   * LE CONSTAT (recette du 2026-07-29)
   * ══════════════════════════════════════════════════════════════════════════
   *
   * « Stop / Pause à T-1 laisse partir l'appel. » Et depuis que `connect()`
   * est anticipé de deux secondes, cette fenêtre existe à CHAQUE contact : un
   * arrêt qui ne rappellerait pas le leg passerait un appel que l'agent vient
   * explicitement de refuser.
   *
   * `dialing` UNIQUEMENT, et c'est la garde qui donne son sens à la
   * fonctionnalité : `ringing` signifie que le correspondant SONNE DÉJÀ. À
   * partir de là il n'y a plus rien à rappeler — il y a un appel à raccrocher,
   * ce qui est un autre geste (et laisse une trace, à juste titre).
   *
   * La préview est RÉ-ARMÉE sur le même contact, **en pause** : rien ne repart
   * sans un geste de l'agent. C'est ce qui rend le contact « conservé » —
   * il reste présenté, et sa réservation n'est pas libérée ici (le serveur la
   * conserve aussi, cf. `abort-launch`).
   *
   * @returns `false` si le poste n'est pas dans cet état précis — l'appelant ne
   *          doit alors surtout pas raccrocher quoi que ce soit.
   */
  abortDialing(params: { countdownSeconds: number }): boolean {
    const { phase, direction, startedAt, campaignContactId, phoneNumber } = state
    if (phase !== 'dialing') return false
    // Un entrant ne se « rappelle » pas, et une conversation entamée non plus.
    if (direction !== 'OUTBOUND' || startedAt !== null) return false
    // Hors campagne il n'y a pas de préview où revenir : un appel manuel se
    // raccroche (chemin nominal), il ne se rembobine pas.
    if (!campaignContactId || !phoneNumber) return false

    const total = Math.max(0, Math.floor(params.countdownSeconds))
    console.warn('[telephony-v2] Leg rappelé avant sonnerie — préview ré-armée en pause', {
      callId: state.callId,
      campaignContactId,
    })
    return transition('preview', {
      // Le `Call` est supprimé côté serveur : plus rien ne le désigne ici.
      callId: null,
      twilioSid: null,
      startedAt: null,
      endedAt: null,
      isMuted: false,
      error: null,
      errorCode: null,
      dialFailure: null,
      preview: {
        remainingSeconds: total,
        totalSeconds: total,
        // EN PAUSE : l'agent vient de demander que rien ne parte.
        paused: true,
        dueFired: false,
      },
    })
  },

  /** Pause / reprise du rebours (bouton de la préview). No-op hors `preview`. */
  setPreviewPaused(paused: boolean): void {
    const { phase, preview } = state
    if (phase !== 'preview' || !preview || preview.paused === paused) return
    setState({ preview: { ...preview, paused } })
  },

  /**
   * L'agent arrête l'enchaînement (ou l'orchestrateur renonce). Rien n'a été
   * composé : retour au repos sec, sans qualification.
   */
  cancelPreview(): void {
    if (state.phase !== 'preview') return
    callMachine.reset()
  },

  // -------------------------------------------------------------------------
  // Entrant
  // -------------------------------------------------------------------------

  /**
   * Un entrant sonne chez cet agent.
   *
   * @returns `true` si l'appel est PRÉSENTÉ à l'agent, `false` s'il doit être
   *          REFUSÉ — dans ce cas `device.ts` appelle `call.reject()`, ce qui
   *          rend la main à Twilio immédiatement (le `<Dial>` continue de
   *          sonner chez les autres agents, ou l'appelant part en file). Un
   *          entrant qu'on ignore en silence, lui, sonne 25 s dans le vide
   *          puis finit en manqué : c'est exactement le constat F-8.
   *
   * Un entrant qui arrive pendant un `wrapup` est ACCEPTÉ (cf. `BUSY_PHASES`) :
   * la saisie en cours est abandonnée, le `Call` précédent reste simplement à
   * qualifier. L'appelant passe avant le formulaire.
   */
  incomingRinging(params: {
    twilioSid: string
    phoneNumber: string
    callId?: string
    contactId?: string
    contactName?: string
  }): boolean {
    if (BUSY_PHASES.includes(state.phase)) {
      console.warn(
        `[telephony-v2] Entrant REFUSÉ proprement (agent en "${state.phase}") — reject() vers Twilio`,
        { incomingSid: params.twilioSid, currentCallId: state.callId },
      )
      return false
    }
    if (state.phase === 'wrapup') {
      console.warn(
        '[telephony-v2] Entrant présenté pendant un wrapup — la qualification en cours est abandonnée',
        { abandonedCallId: state.callId, incomingSid: params.twilioSid },
      )
    }
    if (state.phase === 'preview') {
      console.warn(
        '[telephony-v2] Entrant présenté pendant une préview — le rebours auto-dial est annulé',
        { previewedContactId: state.contactId, incomingSid: params.twilioSid },
      )
    }
    return transition('incoming', {
      twilioSid: params.twilioSid,
      phoneNumber: params.phoneNumber,
      contactId: params.contactId ?? null,
      contactName: params.contactName ?? null,
      // Un entrant n'appartient à aucune campagne : les coordonnées de campagne
      // d'une préview interrompue ne doivent pas déteindre sur lui (script et
      // avancement de campagne d'un contact qu'on n'a pas appelé).
      campaignId: null,
      campaignContactId: null,
      direction: 'INBOUND',
      // Posé par le TwiML `<Parameter name="callId">` — indispensable pour
      // rattacher l'outcome au bon Call après un entrant.
      callId: params.callId ?? null,
      startedAt: null,
      endedAt: null,
      isMuted: false,
      preview: null,
      error: null,
      errorCode: null,
      dialFailure: null,
    })
  },

  // -------------------------------------------------------------------------
  // Progression
  // -------------------------------------------------------------------------

  /** Le destinataire sonne (événement SDK `ringing` ou push WebSocket DIALING). */
  remoteRinging(): boolean {
    return transition('ringing')
  },

  /** Décroché — des deux côtés, sortant comme entrant. */
  connected(): boolean {
    // `startedAt` n'est posé qu'au PREMIER passage : un second `accept`
    // (événement dupliqué) ne doit pas remettre le chronomètre à zéro.
    return transition('active', state.startedAt ? {} : { startedAt: Date.now() })
  },

  /**
   * Fin d'appel.
   *
   * `needsOutcome: false` pour les fins qui n'ont rien à qualifier (entrant
   * annulé par l'appelant avant décroché, entrant pris par un autre agent).
   *
   * `error` : fin SUBIE (erreur SDK sur un appel en conversation). L'appel
   * reste qualifiable — c'est tout l'intérêt du wrapup — et la cause reste
   * affichable à côté du formulaire, au lieu de disparaître avec la machine.
   */
  disconnected(opts: { needsOutcome?: boolean; error?: string } = {}): void {
    const needsOutcome = opts.needsOutcome ?? true
    if (state.phase === 'idle' || state.phase === 'wrapup') return
    // Une préview n'est pas un appel : aucun leg n'existe, il n'y a rien à
    // raccrocher ni à qualifier.
    if (state.phase === 'preview') return

    /**
     * SORTANT JAMAIS DÉCROCHÉ : campagne et manuel suivent désormais le MÊME
     * chemin — le wrapup (règle produit de la recette du 2026-08-15 : « un
     * appel est TOUJOURS qualifié »).
     *
     * L'arbitrage inverse (mineur ② des lentilles de #46 : « MANUEL ⇒ retour
     * au composeur ») a été RETIRÉ ici sur constat de recette : un appel
     * manuel vers un numéro invalide se clôturait en base (`BUSY`) mais la
     * scène ne montrait qu'un bandeau « cause encore inconnue » — jamais mis à
     * jour (à `idle`, `refineDialFailure()` et le push `call.updated` ne
     * concernent plus personne : `callId` est nul) — et AUCUNE qualification
     * n'était possible. Le `Call` existe, `setOutcome` sait le qualifier sans
     * ligne de campagne (c'est le flux des appels manuels répondus) : il n'y a
     * aucune raison de priver l'agent du panneau d'échec.
     */
    const unansweredOutbound =
      state.direction === 'OUTBOUND' && state.startedAt === null

    if (needsOutcome && state.callId) {
      // Sans `callId`, il n'y a rien à qualifier : le wrapup serait un cul-de-sac
      // (aucun POST possible) — on rend directement la main à l'agent.
      transition('wrapup', {
        // Figé ici : la durée affichée dans le formulaire est celle de la
        // conversation, pas celle qui court pendant la saisie.
        endedAt: Date.now(),
        ...(opts.error ? { error: opts.error } : {}),
        /**
         * ⚠️ LA CAUSE RACINE DU « PANNEAU 1-CLIC INVISIBLE » (constat E de la
         * recette du 2026-07-29).
         *
         * L'utilisateur n'a JAMAIS vu le panneau d'échec sur un vrai BUSY :
         * il a dû qualifier au formulaire complet. Le chemin réel n'est pas
         * celui que les tests exerçaient. Sur un numéro invalide, `device.ts`
         * reçoit un 31005 et appelle `endLiveCallOnError`, qui **raccroche le
         * leg AVANT de classer** (`call.disconnect()`, ordre voulu — cf. son
         * commentaire). Le `disconnect` du SDK repasse donc ICI, en PREMIER,
         * et ouvrait un `wrapup` SANS `dialFailure` : la surface était figée
         * sur « formulaire » (règle sticky de `wrapupSurface.ts`, elle-même
         * voulue). `dialFailed()`, arrivé ensuite, ne trouvait plus qu'un
         * wrapup ouvert : `'ignored'`. Et `refineDialFailure()`, plus tard,
         * enrichissait un panneau que personne n'affichait.
         *
         * La règle : **un sortant qui n'a JAMAIS été décroché (`startedAt`
         * nul) et qui se termine EST un échec de numérotation**, quel que soit
         * l'événement qui l'annonce. On ne connaît pas encore la cause — d'où
         * `UNREACHABLE`, sans issue suggérée ; le statut serveur la précisera
         * (`refineDialFailure`), et il enrichira alors une surface DÉJÀ la
         * bonne.
         *
         * Un échec déjà classé n'est jamais écrasé (`state.dialFailure`
         * prime) : le second annonceur ne peut qu'ajouter de l'information.
         *
         * Les sortants MANUELS passent ici aussi (recette du 2026-08-15) :
         * pas de ligne de campagne à faire avancer, mais un `Call` à qualifier
         * — le panneau d'échec s'affiche et `setOutcome` écrit sur le `Call`.
         */
        ...(unansweredOutbound && state.dialFailure === null
          ? { dialFailure: UNREACHABLE_DIAL_FAILURE }
          : {}),
      })
      return
    }
    if (opts.error) {
      // Rien à qualifier, mais l'agent doit savoir POURQUOI l'appel s'est
      // arrêté : `fail()` remet au repos en conservant l'erreur.
      callMachine.fail(opts.error)
      return
    }
    callMachine.reset()
  },

  /**
   * ÉCHEC DE NUMÉROTATION — le leg n'a JAMAIS abouti (occupé, numéro invalide,
   * 31005 « hangup » de la passerelle). Ce n'est pas une panne du poste, c'est
   * un résultat d'appel. Cf. l'en-tête de `dialFailure.ts` pour le constat de
   * recette qui a imposé cette distinction.
   *
   * Un seul chemin dès qu'un `Call` existe : **`wrapup`** (règle produit de la
   * recette du 2026-08-15 — « un appel est TOUJOURS qualifié »).
   *
   * - CAMPAGNE : l'agent qualifie en un clic, `setOutcome()` déclenche
   *   `updateAfterCall()` côté serveur, la ligne de campagne avance et
   *   `GET /contacts/next` sert enfin le contact SUIVANT. Sans cela,
   *   l'enchaînement rebouclait indéfiniment sur le mauvais numéro : c'est
   *   très exactement le gel observé en recette.
   * - MANUEL : même wrapup — `setOutcome` écrit sur le `Call` sans ligne de
   *   campagne (flux des appels manuels répondus). L'ancienne branche « retour
   *   au composeur » laissait l'appel inqualifiable et le bandeau « cause
   *   encore inconnue » à demeure (constat de recette du 2026-08-15).
   *
   * `'released'` ne subsiste que pour l'échec SANS `Call` (rien à qualifier :
   * un formulaire ne pourrait rien poster).
   *
   * @returns `'ignored'` quand l'échec a DÉJÀ été classé (le poste et le
   *          serveur annoncent le même fait, dans un ordre non garanti : le
   *          second arrivé ne doit surtout pas écraser le premier — c'était
   *          l'autre moitié du bug).
   */
  dialFailed(failure: DialFailure): 'wrapup' | 'released' | 'ignored' {
    const { phase, callId } = state
    if (phase !== 'dialing' && phase !== 'ringing') return 'ignored'

    if (callId) {
      transition('wrapup', {
        // Aucune conversation n'a eu lieu : `startedAt` reste null, la durée
        // affichée dans la qualification est donc bien 0.
        endedAt: Date.now(),
        error: `dialFailure.${failure.reason}`,
        dialFailure: failure,
      })
      return 'wrapup'
    }

    callMachine.fail(`dialFailure.${failure.reason}`)
    return 'released'
  },

  /**
   * REPRISE D'UN WRAPUP APRÈS RAFRAÎCHISSEMENT (issue #220, C3).
   *
   * La machine vit en mémoire : un rechargement de la page la remet à `idle`
   * alors que le serveur garde le `Call` ANSWERED + PENDING et compte l'agent
   * « en wrapup » pendant `AGENT_WRAPUP_EXCLUSION_SECONDS` (180 s) — les
   * entrants suivants partent en file puis MISSED, et l'agent n'a plus aucun
   * moyen de qualifier l'appel qui le bloque. Constat prod du 2026-10-12.
   *
   * Transition EXPLICITE `idle → wrapup`, et non un appel simulé : rien n'a
   * sonné, rien n'est connecté — il y a seulement un résultat à saisir. Le
   * provider l'appelle une fois au démarrage, avec ce que le serveur rend
   * (`GET /telephony/wrapup/pending`). Le wrapup restauré se quitte comme tout
   * wrapup : par une issue (`reset`), par la préemption d'un entrant
   * (`incomingRinging`) ou par l'API d'abandon (`abandonWrapup`, sans bouton
   * dans l'UI v2 — un appel est TOUJOURS qualifié).
   *
   * `startedAt` est RECONSTITUÉ depuis la durée serveur (jamais laissé nul) :
   * la scène affiche `endedAt − startedAt`, et `refineDialFailure` ne doit
   * surtout pas prendre un sortant RÉPONDU pour un échec de numérotation
   * (sa garde est `startedAt === null`).
   *
   * @returns `false` si le poste n'est pas au repos — un entrant qui a sonné
   *          pendant la lecture serveur, une préview armée… ne sont jamais
   *          écrasés par une qualification d'hier.
   */
  restoreWrapup(params: {
    callId: string
    direction: 'INBOUND' | 'OUTBOUND' | null
    phoneNumber: string | null
    contactId: string | null
    contactName: string | null
    campaignId: string | null
    campaignContactId: string | null
    /** Durée de la conversation (s) — 0 si le serveur ne la connaît pas. */
    durationSeconds: number
    /** Fin de conversation, timestamp ms. */
    endedAt: number
  }): boolean {
    if (state.phase !== 'idle') {
      console.warn('[telephony-v2] Reprise de wrapup REFUSÉE : le poste n’est pas au repos', {
        phase: state.phase,
        currentCallId: state.callId,
        restoredCallId: params.callId,
      })
      return false
    }
    const duration = Math.max(0, Math.floor(params.durationSeconds || 0))
    return transition('wrapup', {
      callId: params.callId,
      twilioSid: null,
      direction: params.direction,
      phoneNumber: params.phoneNumber,
      contactId: params.contactId,
      contactName: params.contactName,
      campaignId: params.campaignId,
      campaignContactId: params.campaignContactId,
      startedAt: params.endedAt - duration * 1000,
      endedAt: params.endedAt,
      isMuted: false,
      preview: null,
      error: null,
      errorCode: null,
      dialFailure: null,
    })
  },

  /**
   * Le statut backend (`BUSY`, `FAILED`…) arrive APRÈS la classification faite
   * par le poste : il apporte la RAISON exacte et l'issue à suggérer, là où
   * l'erreur SDK ne disait que « ça n'a pas abouti ».
   *
   * Condition : être en `wrapup` sur un appel JAMAIS décroché (`startedAt`
   * nul). C'est le seul discriminant fiable — le wrapup a pu être ouvert par
   * l'erreur SDK, par le push serveur, ou par l'événement `disconnect` du leg,
   * et cette garde couvre les trois ordres d'arrivée sans en privilégier un.
   * Une conversation réelle terminée par un statut tardif n'est pas concernée.
   *
   * `OUTBOUND` uniquement : un ENTRANT jamais décroché est aussi un `wrapup` à
   * `startedAt` nul, et un statut `FAILED` tardif sur cet appel lui collerait
   * « numéro invalide » et proposerait `WRONG_NUMBER` — sur le numéro de
   * quelqu'un qui vient de nous appeler. Il n'y a pas eu de numérotation.
   */
  refineDialFailure(failure: DialFailure): void {
    if (state.direction !== 'OUTBOUND') return
    if (state.phase !== 'wrapup' || state.startedAt !== null) return
    setState({ dialFailure: failure, error: `dialFailure.${failure.reason}` })
  },

  // -------------------------------------------------------------------------
  // Enrichissements (jamais de changement de phase)
  // -------------------------------------------------------------------------

  attachCallId(callId: string): void {
    setState({ callId })
  },

  attachTwilioSid(twilioSid: string): void {
    setState({ twilioSid })
  },

  setMuted(isMuted: boolean): void {
    setState({ isMuted })
  },

  /**
   * Réconciliation après une reconnexion WebSocket : le serveur fait foi sur
   * les identifiants, jamais sur la phase (le SDK local sait mieux que la DB
   * si le micro est ouvert).
   */
  syncFromServer(snapshot: { callId: string; twilioSid?: string | null }): void {
    if (state.callId !== snapshot.callId) return
    if (snapshot.twilioSid && snapshot.twilioSid !== state.twilioSid) {
      setState({ twilioSid: snapshot.twilioSid })
    }
  },

  // -------------------------------------------------------------------------
  // Sorties
  // -------------------------------------------------------------------------

  /**
   * Échec : la machine revient au repos MAIS l'erreur reste affichable.
   * (L'ancienne version appelait `reset()`, qui l'effaçait aussitôt.)
   */
  fail(error: string, errorCode?: string | null): void {
    console.error('[telephony-v2] Appel en échec', {
      errorKey: error,
      errorCode: errorCode ?? null,
      callId: state.callId,
    })
    // `errorCode` (#144) : code Twilio brut à interpoler dans la clé — absent
    // pour tous les appelants historiques (arité conservée).
    state = { ...initialState, error, errorCode: errorCode ?? null }
    notify()
  },

  /**
   * La machine renonce au wrapup et se libère. Depuis la recette du
   * 2026-08-15 (« un appel est TOUJOURS qualifié »), AUCUN bouton ne mène plus
   * ici : ne subsistent que les chemins système (préemption par un entrant via
   * `incomingRinging`, abandon d'une écriture en échec via `outcomeQueue`).
   */
  abandonWrapup(): void {
    if (state.phase !== 'wrapup') return
    console.warn('[telephony-v2] Wrapup abandonné sans outcome', {
      callId: state.callId,
    })
    callMachine.reset()
  },

  /** Efface la bannière d'erreur (nouvelle action de l'agent). */
  clearError(): void {
    if (state.error === null) return
    setState({ error: null, errorCode: null })
  },

  /** Outcome saisi (ou appel abandonné) → retour idle complet. */
  reset(): void {
    state = { ...initialState }
    notify()
  },
}
